import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import PageNavbar from "@/components/feature/PageNavbar";
import Footer from "@/pages/home/components/Footer";

interface TrackedOrder {
  ref: string; created_at: string; status: string;
  items: { name: string; qty: number; line_total: number }[];
  totals: { subtotal: number; delivery: number; vat: number; total: number };
  delivery: { label: string };
  payment: { method: string; status: string; instructions: string };
  history: { at: string; status: string; note: string }[];
  stk_available: boolean;
}

const fmt = (n: number) => `KES ${Math.round(n).toLocaleString()}`;
const STEPS = ["new", "confirmed", "processing", "ready", "dispatched", "delivered"];
const LABEL: Record<string, string> = { new: "Order received", confirmed: "Confirmed", processing: "In fabrication / processing", ready: "Ready", dispatched: "Out for delivery", delivered: "Delivered", cancelled: "Cancelled" };
const PAY: Record<string, string> = { unpaid: "Awaiting payment", pending_verification: "Payment being verified", paid: "Paid", refunded: "Refunded" };

export default function TrackOrderPage() {
  useSEO({ title: "Track your order | ISMAN Engineering Store", description: "Check the status of your ISMAN Engineering store order.", keywords: "ISMAN order tracking", canonical: "https://isman.co.ke/store/order" });
  const [params] = useSearchParams();
  const last = (() => { try { return JSON.parse(localStorage.getItem("isman_last_order") || "{}"); } catch { return {}; } })();
  const [ref, setRef] = useState(params.get("ref") || last.ref || "");
  const [phone, setPhone] = useState(last.phone || "");
  const [order, setOrder] = useState<TrackedOrder | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");

  const call = async (body: Record<string, string>) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await fetch("/api/store/track.php", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ref: ref.trim(), phone, ...body }) });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error);
      setOrder(j.order); if (j.message) setMsg(j.message);
      try { localStorage.setItem("isman_last_order", JSON.stringify({ ref: ref.trim(), phone })); } catch { /* ignore */ }
    } catch (e) { setErr((e as Error).message || "Could not load order"); } finally { setBusy(false); }
  };

  useEffect(() => { if (ref && phone) call({}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stepIdx = order ? STEPS.indexOf(order.status) : -1;

  return (
    <main className="min-h-screen bg-gray-50">
      <PageNavbar />
      <section className="pt-36 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/store" className="text-sm text-teal-700 hover:underline"><i className="ri-arrow-left-line"></i> Back to store</Link>
          <h1 className="text-3xl font-extrabold text-gray-900 mt-3">Track your order</h1>
          <p className="text-gray-500 text-sm mt-1">Enter your order reference (e.g. ISM-ORD-261017-AB3K) and the phone number you used.</p>

          <form onSubmit={(e) => { e.preventDefault(); call({}); }} className="bg-white rounded-xl border border-gray-100 p-5 mt-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3">
            <input value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="Order reference" required className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" required className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm" />
            <button disabled={busy} className="bg-teal-600 hover:bg-teal-500 text-white font-bold px-6 py-2.5 rounded-lg text-sm cursor-pointer disabled:opacity-60">{busy ? "Checking…" : "Track"}</button>
          </form>
          {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}

          {order && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 mt-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-gray-400">Order</p>
                  <p className="font-mono font-extrabold text-teal-700 text-lg">{order.ref}</p>
                  <p className="text-xs text-gray-400">{new Date(order.created_at).toLocaleString("en-GB")}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${order.status === "cancelled" ? "bg-rose-100 text-rose-700" : "bg-teal-100 text-teal-700"}`}>{LABEL[order.status] || order.status}</span>
                  <p className={`text-xs mt-1 font-semibold ${order.payment.status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>{PAY[order.payment.status] || order.payment.status}</p>
                </div>
              </div>

              {order.status !== "cancelled" && (
                <div className="flex items-center mt-6">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex-1 flex flex-col items-center relative">
                      {i > 0 && <div className={`absolute top-3 right-1/2 w-full h-0.5 ${i <= stepIdx ? "bg-teal-500" : "bg-gray-200"}`}></div>}
                      <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${i <= stepIdx ? "bg-teal-600 text-white" : "bg-gray-200 text-gray-500"}`}>{i < stepIdx || (i === stepIdx && s === "delivered") ? <i className="ri-check-line"></i> : i + 1}</div>
                      <p className="text-[10px] text-gray-500 mt-1 text-center leading-tight">{LABEL[s]}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 border-t border-gray-100 pt-4 space-y-1.5 text-sm">
                {order.items.map((i) => (
                  <div key={i.name} className="flex justify-between"><span>{i.name} <span className="text-gray-400">×{i.qty}</span></span><span>{fmt(i.line_total)}</span></div>
                ))}
                <div className="flex justify-between text-gray-500 text-xs pt-2"><span>Delivery — {order.delivery.label}</span><span>{order.totals.delivery ? fmt(order.totals.delivery) : "Free"}</span></div>
                <div className="flex justify-between text-gray-500 text-xs"><span>VAT</span><span>{fmt(order.totals.vat)}</span></div>
                <div className="flex justify-between font-bold text-teal-700"><span>Total</span><span>{fmt(order.totals.total)}</span></div>
              </div>

              {order.payment.status !== "paid" && order.status !== "cancelled" && (
                <div className="mt-5 bg-amber-50 rounded-lg p-4 text-sm text-gray-700">
                  <p className="whitespace-pre-line">{order.payment.instructions}</p>
                  {order.payment.method === "mpesa" && (
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="M-Pesa code e.g. SJK3ABCD12" className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm font-mono bg-white" />
                      <button onClick={() => call({ action: "mpesa_code", code })} disabled={code.length < 8 || busy} className="bg-teal-600 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-50 cursor-pointer">Submit code</button>
                      {order.stk_available && <button onClick={() => call({ action: "stk" })} disabled={busy} className="border border-teal-600 text-teal-700 text-sm font-semibold px-4 py-2 rounded-md cursor-pointer">Send M-Pesa prompt</button>}
                    </div>
                  )}
                </div>
              )}
              {msg && <p className="mt-3 text-sm text-teal-700">{msg}</p>}

              {order.history.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-2">Updates</h3>
                  <ol className="space-y-2">
                    {order.history.slice().reverse().map((h, i) => (
                      <li key={i} className="flex gap-3 text-sm"><span className="text-xs text-gray-400 w-32 shrink-0">{new Date(h.at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</span><span className="text-gray-700">{h.note}</span></li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 mt-6">Need help? Call 072 411 4555 or WhatsApp us with your order reference.</p>
        </div>
      </section>
      <Footer />
    </main>
  );
}
